import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import type { ReportData, Comment } from '@/types'
import toast from 'react-hot-toast'

// Sponsor fields that the shared page shows

// Sponsor fields that the shared page shows
const SPONSOR_FIELDS = [
  { label: 'Sponsor Name', id: 'spRepName' },
  { label: "Sponsor's Community", id: 'spRepCommunity' },
  { label: 'Describe your relationship with your sponsee', id: 'spRepRelationship', type: 'textarea' },
  { label: 'How could it be enhanced?', id: 'spRepEnhance', type: 'textarea' },
  { label: 'How is the licensed minister meeting their Job Description expectations?', id: 'spRepJobDesc', type: 'textarea' },
  { label: 'How is the minister progressing with Spiritual development?', id: 'spRepSpiritual', type: 'textarea' },
  { label: 'Personal development plan implementation and steps for upcoming year', id: 'spRepDevPlan', type: 'textarea' },
  { label: 'Any ethical issues you have supported with this minister?', id: 'spRepEthical', type: 'textarea' },
  { label: 'Areas that could benefit from improvement', id: 'spRepImprovement', type: 'textarea' },
  { label: 'Feedback communicated to your sponsee', id: 'spRepFeedback', type: 'textarea' },
  { label: 'Signature (typed name)', id: 'spRepSignature' },
  { label: 'Date', id: 'spRepDate', type: 'date' },
]

const MENTOR_FIELDS = [
  { label: 'Mentor Name', id: 'mnRepName' },
  { label: "Mentor's Community", id: 'mnRepCommunity' },
  { label: 'Describe your relationship with your mentee', id: 'mnRepRelationship', type: 'textarea' },
  { label: 'How could it be enhanced?', id: 'mnRepEnhance', type: 'textarea' },
  { label: 'How is the licensed minister using their Personal Development plan?', id: 'mnRepDevPlan', type: 'textarea' },
  { label: 'How is the licensed minister demonstrating a healthy ministry?', id: 'mnRepHealthy', type: 'textarea' },
  { label: 'Areas that could benefit from improvement', id: 'mnRepImprovement', type: 'textarea' },
  { label: 'Feedback communicated to your mentee', id: 'mnRepFeedback', type: 'textarea' },
  { label: 'Signature (typed name)', id: 'mnRepSignature' },
  { label: 'Date', id: 'mnRepDate', type: 'date' },
]

export default function SharedReportPage() {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [section, setSection] = useState<'sponsor' | 'mentor'>('sponsor')
  const [ministerName, setMinisterName] = useState('')
  const [data, setData] = useState<ReportData>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [comments, setComments] = useState<Comment[]>([])

  useEffect(() => {
    if (!token) return
    fetch(`/api/shared/${token}`)
      .then(res => {
        if (!res.ok) throw new Error('Invalid or expired link')
        return res.json()
      })
      .then(result => {
        setSection(result.section)
        setMinisterName(result.ministerName || '')
        setData(result.data || {})
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))

    fetch(`/api/shared/${token}/comments`)
      .then(res => res.ok ? res.json() : { comments: [] })
      .then(res => setComments(res.comments || []))
  }, [token])

  const onChange = useCallback((id: string, val: string) => {
    setData(prev => ({ ...prev, [id]: val }))
  }, [])

  const handleAddComment = useCallback(async (sectionId: string, text: string) => {
    const res = await fetch(`/api/shared/${token}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sectionId, text }),
    })
    if (res.ok) {
      const { comment } = await res.json()
      setComments(prev => [...prev, comment])
    } else {
      toast.error('Failed to post comment')
    }
  }, [token])

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const res = await fetch(`/api/shared/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data }),
      })
      if (!res.ok) throw new Error('Failed to submit')
      setSubmitted(true)
    } catch {
      setError('Failed to submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8f5fc' }}>
        <p style={{ color: '#7c4dba', fontSize: 18 }}>Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8f5fc' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>😞</div>
          <h2 style={{ color: '#2d1b4e' }}>Link Invalid</h2>
          <p style={{ color: '#7a6a8a' }}>{error}</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8f5fc' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h2 style={{ color: '#2d1b4e', fontFamily: "'Georgia', serif" }}>Thank You!</h2>
          <p style={{ color: '#5a4a6a', fontSize: 15 }}>
            Your {section} report for {ministerName} has been submitted successfully.
          </p>
        </div>
      </div>
    )
  }

  const fields = section === 'sponsor' ? SPONSOR_FIELDS : MENTOR_FIELDS
  const title = section === 'sponsor' ? 'Sponsor Report' : 'Mentor Report'

  return (
    <div style={{ minHeight: '100vh', background: '#f8f5fc', padding: '40px 20px', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: '#9a80c0', marginBottom: 4 }}>CSL Leesburg</div>
          <h1 style={{ fontFamily: "'Georgia', serif", color: '#2d1b4e', fontSize: 28, margin: '0 0 4px', fontWeight: 400 }}>
            {title}
          </h1>
          {ministerName && (
            <p style={{ color: '#7c6a94', fontSize: 15, fontStyle: 'italic' }}>For {ministerName}</p>
          )}
        </div>

        <div style={{
          background: `radial-gradient(ellipse at 20% 20%, hsla(35,30%,95%,0.6) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, hsla(65,30%,95%,0.4) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, hsla(50,20%,98%,1) 0%, hsla(35,10%,99%,1) 100%)`,
          borderRadius: 20, padding: '32px 28px',
          boxShadow: '0 2px 20px rgba(100,60,140,0.06)',
          border: '1px solid rgba(200,160,60,0.2)',
        }}>
          {fields.map(field => (
            <div key={field.id} style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#4a3660', fontSize: 14, marginBottom: 6 }}>
                  {field.label}
                </label>
                <button 
                  onClick={() => {
                    const el = document.getElementById('new-comment-text') as HTMLTextAreaElement;
                    if (el) {
                      el.value = `${field.label}: `;
                      el.focus();
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  style={{ 
                    background: 'rgba(124,77,186,0.05)', border: '1px solid rgba(124,77,186,0.2)', 
                    cursor: 'pointer', padding: '2px 8px', borderRadius: '8px',
                    fontSize: 12, color: '#7c4dba', transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', gap: 4
                  }}
                  title="Comment on this specific field"
                >
                  🗨️ <span style={{ fontWeight: 600 }}>Comment</span>
                </button>
              </div>
              {field.type === 'textarea' ? (
                <textarea
                  value={data[field.id] || ''}
                  onChange={e => onChange(field.id, e.target.value)}
                  rows={5}
                  style={{
                    width: '100%', padding: 14, borderRadius: 12, border: '1.5px solid #d4c5e8',
                    fontFamily: "'Georgia', serif", fontSize: 15, lineHeight: 1.7, resize: 'vertical',
                    background: 'rgba(255,255,255,0.7)', boxSizing: 'border-box',
                  }}
                />
              ) : (
                <input
                  type={field.type || 'text'}
                  value={data[field.id] || ''}
                  onChange={e => onChange(field.id, e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #d4c5e8',
                    fontFamily: "'Georgia', serif", fontSize: 15, background: 'rgba(255,255,255,0.7)',
                    boxSizing: 'border-box',
                  }}
                />
              )}
            </div>
          ))}

          <div style={{ background: 'rgba(255,255,255,0.5)', borderRadius: 12, padding: 20, marginTop: 20, border: '1px solid rgba(200,160,60,0.2)' }}>
            <p style={{ fontSize: 13, color: '#5a4a3a', margin: '0 0 12px', fontWeight: 600 }}>Terms of Acceptance</p>
            <p style={{ fontSize: 12, color: '#7a6a5a', lineHeight: 1.7, margin: '0 0 16px' }}>
              By submitting, I confirm I have reviewed the minister's annual report, find all information fully accurate, and have met with the minister to review and provide feedback.
            </p>
          </div>

          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button onClick={handleSubmit} disabled={submitting} style={{
              padding: '14px 48px', borderRadius: 30, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, #7c4dba, #9b72cf)', color: '#fff',
              fontSize: 16, fontWeight: 600, boxShadow: '0 4px 15px rgba(124,77,186,0.3)',
              opacity: submitting ? 0.7 : 1,
            }}>
              {submitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </div>

        {/* Comments Section */}
        <div style={{ marginTop: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 24 }}>💬</span>
            <h2 style={{ fontFamily: "'Georgia', serif", color: '#2d1b4e', fontSize: 22, margin: 0 }}>
              Discussion & Questions
            </h2>
          </div>
          
          <div style={{ 
            background: '#fff', 
            borderRadius: 20, 
            padding: 24, 
            boxShadow: '0 2px 15px rgba(0,0,0,0.03)',
            border: '1px solid #eee' 
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
              {comments.length === 0 ? (
                <p style={{ color: '#8a7a9a', fontStyle: 'italic', textAlign: 'center', margin: '20px 0' }}>
                  No comments yet. Have a question for the minister?
                </p>
              ) : (
                comments.map(c => (
                  <div key={c.commentId} style={{ 
                    background: '#fcfaff', 
                    padding: '14px 18px', 
                    borderRadius: 16,
                    borderLeft: `4px solid ${c.role === 'minister' ? '#9b72cf' : c.role === 'sponsor' ? '#d4a853' : '#4a9c7f'}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#4a3660' }}>
                        {c.userName} <span style={{ fontWeight: 400, color: '#8a7a9a', fontSize: 11 }}>({c.role})</span>
                      </span>
                      <span style={{ fontSize: 11, color: '#b0a0c0' }}>
                        {(() => {
                          const d = new Date(c.createdAt);
                          return isNaN(d.getTime()) ? 'Just now' : `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                        })()}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: 14, color: '#3a2a50', lineHeight: 1.6 }}>{c.text}</p>
                  </div>
                ))
              )}
            </div>

            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#4a3660', marginBottom: 12 }}>Add a comment</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <textarea
                  placeholder="Type your question or comment here..."
                  style={{
                    flex: 1, padding: 14, borderRadius: 12, border: '1.5px solid #d4c5e8',
                    fontFamily: 'inherit', fontSize: 14, minHeight: 80, resize: 'vertical'
                  }}
                  id="new-comment-text"
                />
                <button 
                  onClick={() => {
                    const el = document.getElementById('new-comment-text') as HTMLTextAreaElement;
                    if (el.value.trim()) {
                      handleAddComment(section, el.value);
                      el.value = '';
                    }
                  }}
                  style={{
                    alignSelf: 'flex-end',
                    background: '#7c4dba', color: '#fff', border: 'none', borderRadius: 12,
                    padding: '12px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    boxShadow: '0 4px 10px rgba(124,77,186,0.2)'
                  }}
                >
                  Post
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
